import { ReactElement, useCallback, useEffect, useState } from 'react';
import SandboxPromiseModal from '@/framework/events/SandboxPromiseModal';
import { useTranslation, useWallet } from '@/services';
import { Button, message } from 'antd';
import styles from '@/styles/pages/admin/sandbox-modal.module.less';
import LocalImage from '@/components/LocalImage';
import { createTicket, getDappInfo, subscribeDappNo, updatePadding, getTicketPayStatus } from '@/services';
import TokenName from '@/components/token/TokenName';
import { useAppTicketController } from '@/services/contracts';
import { ethers } from 'ethers';
import { formatString, sleep } from '@/utils';
import CustomModal from '@/components/CustomModal';
import { normalizeTransactionError } from '@/services/transaction-error';
import { payDappTicket } from '@/services/ticket-payment-flow';
interface PayTicketInfo {
    DAppNo: string;
    dappName: string;
    ticketPirce: string;
    ticketType: string;
    ticketToken: string;
    dappInfo: DappVo;
    title: string;
}
interface TicketPayStatus {
    ticketNo: string;
    payStatus: boolean;
    padding: boolean;
    dappNo: string;
}
const PayTicketModal = (): ReactElement => {
    const { t } = useTranslation('admin', 'admin.common');
    const { t: tt } = useTranslation('common', 'transactions');
    const { logged } = useWallet();
    const { payTicket, subscribe } = useAppTicketController();
    const [dappNo, setDappNo] = useState(undefined as undefined | SandboxPromiseModalData<string, PayTicketInfo>);
    const [payTicketInfo, setPayTicketInfo] = useState(undefined as undefined | PayTicketInfo);
    const [ticketPayStatus, setTicketPayStatus] = useState(undefined as undefined | TicketPayStatus);
    //初始化准备数据
    const initData = useCallback(async () => {
        if (!dappNo) {
            return;
        }
        const dappInfo = await getDappInfo(dappNo.Data.DAppNo);
        setPayTicketInfo({ dappInfo: dappInfo, dappName: dappInfo.dappName, DAppNo: dappNo.Data.DAppNo, ticketPirce: dappInfo.ticketPrice, ticketType: dappInfo.ticketPayment, ticketToken: dappInfo.ticketToken, title: formatString(t('ticket-title'), dappInfo.dappName) });
    }, [dappNo, setPayTicketInfo]);

    useEffect(() => {
        if (!logged) {
            return;
        }
        if (!dappNo) {
            setDappNo(undefined);
            SandboxPromiseModal.On('Sandbox.Ticket.Create', setDappNo);
        }
        initData();
        return () => {
            SandboxPromiseModal.Remove('Sandbox.Ticket.Create');
        };
    }, [logged, setDappNo, initData]);
    //取消
    const onCancel = useCallback(() => {
        if (!dappNo) {
            return;
        }
        dappNo.Reject('User cancel.');
        setDappNo(undefined);
    }, [setDappNo, dappNo]);

    const onOk = useCallback(async () => {
        if (!dappNo || !payTicketInfo) {
            return;
        }
        try {
            setTicketPayStatus({ dappNo: payTicketInfo.DAppNo, ticketNo: '', padding: true, payStatus: false });
            const result = await payDappTicket({
                dappNo: payTicketInfo.DAppNo,
                payment: payTicketInfo.ticketType === 'subscribe' ? 'subscribe' : 'once',
                parseAmount: (amount) => ethers.utils.parseUnits(amount, 'ether'),
                createTicket,
                subscribeDappNo,
                payTicket,
                subscribe,
                updatePadding,
                getTicketPayStatus: async (dappNo, ticketNo) => {
                    const [, status] = await Promise.all([sleep(2000), getTicketPayStatus(dappNo, ticketNo)]);
                    return status;
                },
            });
            setTicketPayStatus({ dappNo: result.dappNo, ticketNo: result.ticketNo, padding: false, payStatus: true });
            dappNo.Resolve('Success');
            setDappNo(undefined);
        } catch (error) {
            const normalizedError = normalizeTransactionError(error);
            const errorMessage = normalizedError.fallbackMessage || tt(normalizedError.messageKey);
            setTicketPayStatus(undefined);
            message.error(errorMessage);
            dappNo.Reject(errorMessage);
            setDappNo(undefined);
        }
    }, [setDappNo, dappNo, setTicketPayStatus, createTicket, payTicketInfo, subscribe, payTicket, tt]);

    return (
        <CustomModal
            footer={[
                <Button className={styles['cancel-btn']} key="back" loading={ticketPayStatus?.padding} onClick={onCancel}>
                    {t('cancel')}
                </Button>,
                <Button className={styles['submit-btn']} key="submit" loading={ticketPayStatus?.padding} onClick={onOk}>
                    {t('sub-btn-name')}
                </Button>,
            ]}
            closable={!ticketPayStatus?.padding}
            maskClosable={false}
            centered
            styles={{ body: styles }}
            closeIcon={<LocalImage dir="/images/wallet" name="close-x" type="svg" width="20" />}
            title={payTicketInfo?.title}
            onCancel={onCancel}
            open={dappNo != undefined}>
            {payTicketInfo === undefined ? <div className={styles['title']}>{t('data-loading')}</div> : ticketPayStatus?.padding ? <div className={styles['title']}>{t('wait-padding')}</div> : <OnceTicketModel dappName={payTicketInfo?.dappName} ticketType={payTicketInfo?.ticketType} token={payTicketInfo?.ticketToken} ticketPirce={payTicketInfo?.ticketPirce} />}
        </CustomModal>
    );
};
const OnceTicketModel = (props: any): ReactElement => {
    const { t } = useTranslation('admin', 'admin.common');
    const type = props.ticketType === 'once' ? t('ticket-type-once') : t('ticket-type-subscribe');
    return (
        <div className={styles['title']}>
            <p key={1}>
                {formatString(t('ticket-remark'), type, props.dappName, props.ticketPirce)}
                <TokenName token={props.token} />
            </p>
        </div>
    );
};

export default PayTicketModal;
